/**
 * Patient Management Hooks
 * TanStack Query hooks for patient CRUD operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type {
  DentalOfficePatient,
  ApiResponse,
  DentalOfficeAppointment,
  DentalOfficeTreatment,
  DentalOfficeInvoice,
  DentalOfficeImage,
} from '../types';

// Query keys
export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (filters: PatientFilters) => [...patientKeys.lists(), filters] as const,
  details: () => [...patientKeys.all, 'detail'] as const,
  detail: (id: number) => [...patientKeys.details(), id] as const,
  appointments: (id: number) => [...patientKeys.detail(id), 'appointments'] as const,
  treatments: (id: number) => [...patientKeys.detail(id), 'treatments'] as const,
  invoices: (id: number) => [...patientKeys.detail(id), 'invoices'] as const,
  images: (id: number) => [...patientKeys.detail(id), 'images'] as const,
};

// Types
export interface PatientFilters {
  search?: string;
  has_myhealth?: boolean;
  status?: 'active' | 'inactive';
  sort_by?: 'name' | 'created_at' | 'last_visit';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CreatePatientInput {
  first_name: string;
  last_name: string;
  kennitala: string;
  email?: string;
  phone: string;
  date_of_birth: string;
  address?: string;
  city?: string;
  postal_code?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  allergies?: string;
  medical_conditions?: string;
  current_medications?: string;
  insurance_provider?: string;
  insurance_number?: string;
  notes?: string;
  preferred_language?: 'is' | 'en';
}

export type UpdatePatientInput = Partial<CreatePatientInput>;

// ============================================================================
// LIST PATIENTS
// ============================================================================

export function usePatientsQuery(filters: PatientFilters = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: patientKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.has_myhealth !== undefined)
        params.set('has_myhealth', String(filters.has_myhealth));
      if (filters.status) params.set('status', filters.status);
      if (filters.sort_by) params.set('sort_by', filters.sort_by);
      if (filters.sort_order) params.set('sort_order', filters.sort_order);
      params.set('limit', String(filters.limit || 20));
      params.set('offset', String(filters.offset || 0));

      const response = await api.get<
        ApiResponse<DentalOfficePatient[]>
      >(`/patients?${params.toString()}`, token || undefined);

      return response;
    },
    enabled: !!token,
  });
}

// ============================================================================
// GET SINGLE PATIENT
// ============================================================================

export function usePatientQuery(id: number | undefined) {
  const { token } = useAuth();

  return useQuery({
    queryKey: patientKeys.detail(id!),
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<DentalOfficePatient>
      >(`/patients/${id}`, token || undefined);
      return response.data;
    },
    enabled: !!token && !!id,
  });
}

// ============================================================================
// GET PATIENT APPOINTMENTS
// ============================================================================

export function usePatientAppointmentsQuery(id: number | undefined) {
  const { token } = useAuth();

  return useQuery({
    queryKey: patientKeys.appointments(id!),
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<DentalOfficeAppointment[]>
      >(`/patients/${id}/appointments`, token || undefined);
      return response.data;
    },
    enabled: !!token && !!id,
  });
}

// ============================================================================
// GET PATIENT TREATMENTS
// ============================================================================

export function usePatientTreatmentsQuery(id: number | undefined) {
  const { token } = useAuth();

  return useQuery({
    queryKey: patientKeys.treatments(id!),
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<DentalOfficeTreatment[]>
      >(`/treatments/patient/${id}`, token || undefined);
      return response.data;
    },
    enabled: !!token && !!id,
  });
}

// ============================================================================
// GET PATIENT INVOICES
// ============================================================================

export function usePatientInvoicesQuery(id: number | undefined) {
  const { token } = useAuth();

  return useQuery({
    queryKey: patientKeys.invoices(id!),
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<DentalOfficeInvoice[]>
      >(`/patients/${id}/invoices`, token || undefined);
      return response.data;
    },
    enabled: !!token && !!id,
  });
}

// ============================================================================
// GET PATIENT IMAGES
// ============================================================================

export function usePatientImagesQuery(id: number | undefined) {
  const { token } = useAuth();

  return useQuery({
    queryKey: patientKeys.images(id!),
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<DentalOfficeImage[]>
      >(`/images/patient/${id}`, token || undefined);
      return response.data;
    },
    enabled: !!token && !!id,
  });
}

// ============================================================================
// CREATE PATIENT
// ============================================================================

export function useCreatePatient() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePatientInput) => {
      const response = await api.post<
        ApiResponse<DentalOfficePatient>
      >('/patients', data, token || undefined);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all patient lists
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
}

// ============================================================================
// UPDATE PATIENT
// ============================================================================

export function useUpdatePatient(id: number) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdatePatientInput) => {
      const response = await api.put<
        ApiResponse<DentalOfficePatient>
      >(`/patients/${id}`, data, token || undefined);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate patient lists and detail
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patientKeys.detail(id) });
    },
  });
}

// ============================================================================
// DELETE PATIENT (Soft Delete)
// ============================================================================

export function useDeletePatient() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/patients/${id}`, token || undefined);
    },
    onSuccess: () => {
      // Invalidate all patient lists
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
}
